// src/features/scheduleGenerator/ScheduleGeneratorProvider.tsx
import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { format, addDays } from 'date-fns';
import { useScheduleGenerator } from './hooks/useScheduleGenerator';
import { ScheduleGeneratorHookContext } from './hooks/useScheduleGenerator';
import ScheduleGeneratorSettingsModal from './components/ScheduleGeneratorSettingsModal';
import ScheduleGeneratorPreviewModal from './components/ScheduleGeneratorPreviewModal';
import ToneSelectionModal from './components/ToneSelectionModal';
import SkeletonModal from './components/SkeletonModal';
import TaskAssignmentModal from './components/TaskAssignmentModal';
import { getCurrentUserId } from '../../services/auth';
import { ScheduleItem } from '../../types/scheduleItem';
import { PlanningSessionManager } from './utils/planningSessionManager';
import { useAIUsageLimit, AIFeature } from '../../hooks/useAIUsageLimit';
import { useUpgradePrompt } from '../../components/subscription/UpgradePromptModal';
import { logEvent } from '../../utils/analytics';
import { ScheduleGeneratorContext, ScheduleGeneratorTriggerOptions, DayDraftOptions } from './ScheduleGeneratorContext';
import { logger } from '../../utils/logger';
import { eventBus, EVENTS } from '../../utils/eventBus';
import { useOnboarding } from '../onboarding';
import { useDayPhase } from '../../hooks/useDayPhase';

export const ScheduleGeneratorProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const scheduleGenerator = useScheduleGenerator();
  const { getActiveLessonGuide } = useOnboarding();
  const isLesson2Active = getActiveLessonGuide() === 'lesson-2';
  const dayPhase = useDayPhase();

  const callbacksRef = useRef<ScheduleGeneratorTriggerOptions>({});
  const dayDraftCallbackRef = useRef<(() => void) | null>(null);

  const { canUseFeature, showUpgradeModal } = useAIUsageLimit();
  const { UpgradeModal } = useUpgradePrompt();

  // ======================================================
  // 統一的觸發接口
  // ======================================================

  const triggerAIPlan = useCallback((context: ScheduleGeneratorHookContext, options?: ScheduleGeneratorTriggerOptions) => {
    logger.info('ScheduleGeneratorProvider triggered', context.source, context.date);

    if (options) {
      callbacksRef.current = options;
    }

    // 檢查 AI 使用權限
    if (!canUseFeature(AIFeature.AI_PLANNING)) {
      logger.info('ScheduleGeneratorProvider: AI Planning usage limit reached, showing upgrade prompt');
      showUpgradeModal(AIFeature.AI_PLANNING);
      return;
    }

    // 開啟設定 Modal
    eventBus.publish(EVENTS.UI.AI_PLAN_TRIGGERED);
    scheduleGenerator.openSettings();
  }, [scheduleGenerator, canUseFeature, showUpgradeModal]);

  // ======================================================
  // 生成計畫處理
  // ======================================================

  const handleGeneratePlan = useCallback(async (date: string, latestNote: string) => {
    try {
      if (latestNote.trim()) {
        const uid = getCurrentUserId() || '';
        PlanningSessionManager.setLatestNote(uid, date, latestNote.trim());
      }

      await scheduleGenerator.generatePlan(date, latestNote);

      try {
        logEvent('ai_plan_generated', {});
      } catch (error) {
        logger.error('ScheduleGeneratorProvider: logEvent failed', error);
      }
    } catch (error) {
      logger.error('ScheduleGeneratorProvider: Failed to generate plan', error);
    }
  }, [scheduleGenerator]);

  // ======================================================
  // 應用計畫處理
  // ======================================================

  const handleApplyPlan = useCallback(async (scheduleItems: ScheduleItem[]) => {
    await scheduleGenerator.applyPlan(scheduleItems, () => {
      callbacksRef.current.onDataRefresh?.();
      callbacksRef.current.onPlanApplied?.();
    });
  }, [scheduleGenerator]);

  // ======================================================
  // 計算載入狀態
  // ======================================================

  const modalIsLoading = scheduleGenerator.isGeneratingPlan;

  // ======================================================
  // 監聽 Modals 關閉狀態
  // ======================================================

  useEffect(() => {
    if (!scheduleGenerator.isAIPlanSettingsModalOpen && !scheduleGenerator.isAIPlanPreviewModalOpen) {
      const timeoutId = setTimeout(() => {
        callbacksRef.current.onClose?.();
        callbacksRef.current = {}; // Reset callbacks after execution
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [scheduleGenerator.isAIPlanSettingsModalOpen, scheduleGenerator.isAIPlanPreviewModalOpen]);

  // ======================================================
  // 渲染
  // ======================================================

  const triggerDayDraft = useCallback((options?: DayDraftOptions) => {
    if (!canUseFeature(AIFeature.AI_PLANNING)) {
      showUpgradeModal(AIFeature.AI_PLANNING);
      return;
    }
    const defaultDate = dayPhase === 'evening'
      ? format(addDays(new Date(), 1), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');
    dayDraftCallbackRef.current = options?.onApplied ?? null;
    scheduleGenerator.startDraft(defaultDate);
  }, [scheduleGenerator, canUseFeature, showUpgradeModal, dayPhase]);

  const stableToneOptions = useMemo(
    () => scheduleGenerator.toneState?.options ?? [],
    [scheduleGenerator.toneState]
  );

  const isApplyingSkeleton = scheduleGenerator.draftStep === 'APPLYING' && scheduleGenerator.assignState === null;
  const isApplyingTasks = scheduleGenerator.draftStep === 'APPLYING' && scheduleGenerator.assignState !== null;

  return (
    <ScheduleGeneratorContext.Provider value={{ triggerAIPlan, triggerDayDraft }}>
      {children}

      {/* 統一管理的 Modal 組件 */}
      <ScheduleGeneratorSettingsModal
        isOpen={scheduleGenerator.isAIPlanSettingsModalOpen}
        onClose={scheduleGenerator.closeSettings}
        onGenerate={handleGeneratePlan}
        isLoading={modalIsLoading}
        isLesson2Active={isLesson2Active}
      />

      <ScheduleGeneratorPreviewModal
        isOpen={scheduleGenerator.isAIPlanPreviewModalOpen}
        onClose={scheduleGenerator.closePreview}
        plan={scheduleGenerator.currentPlan ? {
          ...scheduleGenerator.currentPlan,
          summary: scheduleGenerator.planError || scheduleGenerator.currentPlan.summary || '',
        } : null}
        isLoading={scheduleGenerator.isGeneratingPlan || scheduleGenerator.isApplyingPlan}
        onApply={handleApplyPlan}
        onRegenerate={scheduleGenerator.regeneratePlan}
        isLesson2Active={isLesson2Active}
      />

      {/* Day Draft V3 Modals */}
      <ToneSelectionModal
        isOpen={scheduleGenerator.draftStep === 'TONE_LOADING' || scheduleGenerator.draftStep === 'TONE_READY'}
        onClose={scheduleGenerator.cancelDraft}
        summary={scheduleGenerator.toneState?.summary ?? null}
        options={stableToneOptions}
        isLoading={scheduleGenerator.draftStep === 'TONE_LOADING'}
        onSelect={scheduleGenerator.selectTone}
        draftDate={scheduleGenerator.draftDate ?? ''}
        onDateChange={scheduleGenerator.startDraft}
      />

      <SkeletonModal
        isOpen={
          scheduleGenerator.draftStep === 'SKELETON_GENERATING' ||
          scheduleGenerator.draftStep === 'SKELETON_READY' ||
          isApplyingSkeleton
        }
        onClose={scheduleGenerator.cancelDraft}
        date={scheduleGenerator.draftDate ?? ''}
        timeBlocks={scheduleGenerator.skeletonState?.timeBlocks ?? []}
        timedEvents={scheduleGenerator.skeletonState?.timedEvents ?? []}
        alldayEvents={scheduleGenerator.skeletonState?.alldayEvents ?? []}
        isGenerating={scheduleGenerator.draftStep === 'SKELETON_GENERATING'}
        isApplying={isApplyingSkeleton}
        onFinish={() => scheduleGenerator.finishSkeleton()}
        onFillTasks={scheduleGenerator.startDraftTaskRecommend}
        onBack={scheduleGenerator.goBackToTone}
        onFineTune={scheduleGenerator.fineTuneSkeleton}
      />

      <TaskAssignmentModal
        isOpen={
          scheduleGenerator.draftStep === 'TASK_RECOMMENDING' ||
          scheduleGenerator.draftStep === 'TASK_READY' ||
          scheduleGenerator.draftStep === 'TASK_ASSIGNING' ||
          isApplyingTasks
        }
        onClose={scheduleGenerator.cancelDraft}
        phase={scheduleGenerator.assignState ? 'assign' : 'recommend'}
        isLoading={
          scheduleGenerator.draftStep === 'TASK_RECOMMENDING' ||
          scheduleGenerator.draftStep === 'TASK_ASSIGNING' ||
          isApplyingTasks
        }
        isApplying={isApplyingTasks}
        recommendations={scheduleGenerator.taskState?.recommendations ?? []}
        availableTasks={scheduleGenerator.taskState?.tasks ?? []}
        assignedTimeBoxes={scheduleGenerator.assignState?.timeBoxes ?? []}
        onBack={scheduleGenerator.goBackToSkeleton}
        onConfirmSelection={scheduleGenerator.assignTasks}
        onApply={() => scheduleGenerator.applyDraft(() => {
          dayDraftCallbackRef.current?.();
          dayDraftCallbackRef.current = null;
        })}
        error={scheduleGenerator.draftError}
      />

      {/* 升級提示 Modal */}
      <UpgradeModal />
    </ScheduleGeneratorContext.Provider>
  );
};
